import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { fetchBookings, fetchBookingsCounts, downloadBookingsExcel, Booking } from "@/api/bookings";
import { fetchTasks } from "@/api/dashboard";
import { getSettingsPrices } from "@/api/settings";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { UnitChip } from "@/components/UnitChip";
import { Btn } from "@/components/Btn";
import { useDateRange } from "@/lib/useDateRange";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { formatDatum } from "@/lib/format";

// Tab-Modell: ein Tab pro Dashboard-Aufgabe + "Alle"/"Storniert"/"Intern".
// Status-basierte Tabs gibt es nicht mehr — die Aufgaben sind die primäre Sicht.
type Tab =
  | "alle"
  | "konflikt"
  | "bestaetigung_offen"
  | "konus_link_offen"
  | "konus_daten_offen"
  | "infos_gruppe_offen"
  | "abreise_pruefen"
  | "anzahlung_offen"
  | "storno_rechnung_offen"
  | "rechnung_offen"
  | "rechnung_eingang_offen"
  | "storniert"
  | "intern";

const TAB_LABELS: Record<Tab, string> = {
  alle: "Alle",
  konflikt: "Konflikte",
  bestaetigung_offen: "Bestätigung offen",
  konus_link_offen: "Konus-Link",
  konus_daten_offen: "Konus-Daten",
  infos_gruppe_offen: "Infos versenden",
  abreise_pruefen: "Abreise prüfen",
  anzahlung_offen: "Anzahlung offen",
  storno_rechnung_offen: "Storno-Rechnung",
  rechnung_offen: "Rechnung offen",
  rechnung_eingang_offen: "Rechnungseingang",
  storniert: "Storniert",
  intern: "Intern",
};

// Tabs, die einen `task_filter`-Parameter zum Backend schicken.
const TAB_TASK_KEY: Partial<Record<Tab, string>> = {
  konflikt: "konflikt",
  bestaetigung_offen: "bestaetigung_offen",
  konus_link_offen: "konus_link_offen",
  konus_daten_offen: "konus_daten_offen",
  infos_gruppe_offen: "infos_gruppe_offen",
  abreise_pruefen: "abreise_pruefen",
  anzahlung_offen: "anzahlung_offen",
  storno_rechnung_offen: "storno_rechnung_offen",
  rechnung_offen: "rechnung_offen",
  rechnung_eingang_offen: "rechnung_eingang_offen",
};

// Tabs, die einen Status-Filter benutzen (statt Task-Filter).
const TAB_STATUS: Partial<Record<Tab, string>> = {
  storniert: "Storniert",
  intern: "Intern",
};

const VALID_TABS = new Set<string>([
  "alle",
  "konflikt",
  "bestaetigung_offen",
  "konus_link_offen",
  "konus_daten_offen",
  "infos_gruppe_offen",
  "abreise_pruefen",
  "anzahlung_offen",
  "storno_rechnung_offen",
  "rechnung_offen",
  "rechnung_eingang_offen",
  "storniert",
  "intern",
]);

// Auswählbare Status-Werte (siehe domain/booking/workflow.py).
const STATUS_OPTIONS: string[] = [
  "Angelegt",
  "Bestätigt",
  "Konus Link versandt",
  "Konus-Daten eingegeben",
  "Infos an Gruppe versandt",
  "Abgereist",
  "Rechnung erstellt",
  "Bezahlt",
  "Storniert",
  "Intern",
];

const ITEMS_PER_PAGE = 10;

function naechte(checkIn: string, checkOut: string): number {
  const d1 = new Date(checkIn).getTime();
  const d2 = new Date(checkOut).getTime();
  return Math.round((d2 - d1) / 86400000);
}

export function BookingsPage() {
  useDocumentTitle("Buchungen");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") ?? "alle") as Tab;
  const [tab, setTab] = useState<Tab>(VALID_TABS.has(initialTab) ? initialTab : "alle");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Filter-State
  const [filterEinheiten, setFilterEinheiten] = useState<number[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterVon, setFilterVon] = useState("");
  const [filterBis, setFilterBis] = useState("");
  const filterDateRange = useDateRange(
    filterVon,
    filterBis,
    ({ from, to }) => { setFilterVon(from); setFilterBis(to); setPage(1); },
    { minNights: 0, keepDuration: false },
  );
  const [exporting, setExporting] = useState(false);

  // Einheitenliste aus Settings
  const { data: settingsPrices } = useQuery({
    queryKey: ["settings-prices"],
    queryFn: getSettingsPrices,
    retry: false,
  });
  const einheiten = settingsPrices?.einheiten ?? [];

  const hasActiveFilter =
    filterEinheiten.length > 0 ||
    filterStatus.length > 0 ||
    filterVon !== "" ||
    filterBis !== "";

  // Tab-spezifische Filter werden serverseitig angewandt — passt zur Logik
  // in domain/booking/dashboard_metrics.task_filter_sql() (Single Source of Truth).
  const taskFilter = TAB_TASK_KEY[tab];
  const statusFilterForTab = TAB_STATUS[tab];

  // Server-seitiger Status-Filter: Tab-Status hat Priorität; sonst der User-Filter.
  const statusFilterServer =
    statusFilterForTab ?? (filterStatus.length > 0 ? filterStatus.join(",") : undefined);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["bookings", tab, filterStatus, filterVon, filterBis],
    queryFn: () => fetchBookings({
      task_filter: taskFilter,
      status_filter: statusFilterServer,
      von: filterVon || undefined,
      bis: filterBis || undefined,
    }),
    retry: false,
  });

  // Tabs, die immer sichtbar sind — auch wenn der Count 0 ist oder unbekannt.
  // Task-Tabs mit Count 0 blenden wir aus, um die Tab-Leiste schlank zu halten.
  const ALWAYS_VISIBLE: ReadonlySet<Tab> = new Set(["alle", "storniert", "intern"]);

  // Counts pro Tab kommen aus dem Dashboard-Endpoint (für Task-Tabs) bzw.
  // gebündelt aus /api/bookings/counts (Alle/Storniert/Intern/Konflikt).
  const { data: taskCounts } = useQuery({
    queryKey: ["dashboard", "tasks"],
    queryFn: fetchTasks,
    retry: false,
  });
  const { data: tabCounts } = useQuery({
    queryKey: ["bookings", "counts"],
    queryFn: fetchBookingsCounts,
    retry: false,
  });

  // Client-seitige Filterung: Einheit-Multi + Suche. Status/Zeitraum laufen serverseitig.
  const allBookings = (data ?? [])
    .filter((b) => {
      if (filterEinheiten.length === 0) return true;
      return b.einheiten_ids.some((id) => filterEinheiten.includes(id));
    })
    .filter((b) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        b.gruppenname.toLowerCase().includes(q) ||
        b.gastname.toLowerCase().includes(q) ||
        b.nachname.toLowerCase().includes(q) ||
        b.vorname.toLowerCase().includes(q) ||
        b.buchungsreferenz.toLowerCase().includes(q) ||
        (b.email ?? "").toLowerCase().includes(q)
      );
    });

  const getCount = (t: Tab): number => {
    if (t === "konflikt" || t === "storniert" || t === "intern" || t === "alle") {
      return tabCounts?.[t] ?? 0;
    }
    const taskKey = TAB_TASK_KEY[t];
    if (taskKey) {
      return taskCounts?.find((x) => x.key === taskKey)?.count ?? 0;
    }
    return 0;
  };

  // Pagination
  const totalPages = Math.ceil(allBookings.length / ITEMS_PER_PAGE);
  const paginatedBookings = allBookings.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <div>
      <PageHeader
        title="Buchungen"
        subtitle={`${allBookings.length} ${allBookings.length === 1 ? "Buchung" : "Buchungen"}`}
        actions={
          <>
            <Btn
              variant="secondary"
              icon="download"
              disabled={exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  await downloadBookingsExcel({
                    task_filter: taskFilter,
                    status_filter: statusFilterServer,
                    von: filterVon || undefined,
                    bis: filterBis || undefined,
                  });
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? "…" : "Excel"}
            </Btn>
            <Btn
              variant="primary"
              icon="add"
              onClick={() => navigate("/bookings/new")}
            >
              Neue Buchung
            </Btn>
          </>
        }
      />

      {/* Tabs + Search */}
      <div style={{ marginBottom: 16 }}>
        {/* Tab-Leiste */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 2, background: "var(--bg-subtle)", borderRadius: 10, padding: 4, marginBottom: 16 }}>
          {(Object.keys(TAB_LABELS) as Tab[])
            .filter((t) => ALWAYS_VISIBLE.has(t) || t === tab || getCount(t) > 0)
            .map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setPage(1);
                // Tab in die URL spiegeln, damit Zurück aus der Detailseite
                // (via location.search) den richtigen Tab wiederherstellt.
                setSearchParams(t === "alle" ? {} : { tab: t }, { replace: true });
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                fontWeight: 600,
                background: tab === t ? "var(--bg-card)" : "transparent",
                color: tab === t ? "var(--fg)" : "var(--fg-muted)",
                boxShadow: tab === t ? "0 1px 3px rgba(0,0,0,.08)" : "none",
                transition: "all .12s",
                whiteSpace: "nowrap",
              }}
            >
              {TAB_LABELS[t]} <span style={{ fontWeight: 400, marginLeft: 4 }}>({getCount(t)})</span>
            </button>
          ))}
        </div>

        {/* Zeile 1: Suche */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: 16, color: "var(--fg-muted)" }}>
              search
            </span>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buchungsref., Gast oder Gruppe..."
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                color: "var(--fg)",
              }}
            />
            {search && (
              <button
                onClick={() => { setSearch(""); setPage(1); }}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, lineHeight: 1 }}
              >
                <span className="material-icons-outlined" style={{ fontSize: 16, color: "var(--fg-muted)" }}>close</span>
              </button>
            )}
          </div>
        </div>

        {/* Zeile 2: Filter */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--fg-muted)", whiteSpace: "nowrap", marginRight: 4 }}>Filter:</span>

          {/* Einheit (Multi) */}
          <MultiCheckDropdown
            label="Einheiten"
            options={einheiten.map((e) => ({ value: e.id, label: e.name }))}
            selected={filterEinheiten}
            onChange={(vals) => { setFilterEinheiten(vals); setPage(1); }}
            allLabel="Alle Einheiten"
          />

          {/* Status (Multi) */}
          <MultiCheckDropdown
            label="Status"
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            selected={filterStatus}
            onChange={(vals) => { setFilterStatus(vals); setPage(1); }}
            allLabel="Alle Status"
            disabled={statusFilterForTab !== undefined}
            disabledHint={statusFilterForTab !== undefined ? `Tab '${TAB_LABELS[tab]}' aktiv` : undefined}
          />

          {/* Check-in ab */}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--fg-muted)", whiteSpace: "nowrap" }}>
            ab
            <input
              type="date"
              value={filterVon}
              max={filterDateRange.fromProps.max}
              onChange={(e) => filterDateRange.setFrom(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: filterVon ? "var(--accent)" : "var(--bg-card)",
                color: filterVon ? "#fff" : "var(--fg)",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                colorScheme: "light dark",
              }}
            />
          </label>

          {/* Check-in bis */}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--fg-muted)", whiteSpace: "nowrap" }}>
            bis
            <input
              type="date"
              value={filterBis}
              min={filterDateRange.toProps.min}
              onChange={(e) => filterDateRange.setTo(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: filterBis ? "var(--accent)" : "var(--bg-card)",
                color: filterBis ? "#fff" : "var(--fg)",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                colorScheme: "light dark",
              }}
            />
          </label>

          {/* Zurücksetzen */}
          {hasActiveFilter && (
            <button
              onClick={() => {
                setFilterEinheiten([]);
                setFilterStatus([]);
                setFilterVon("");
                setFilterBis("");
                setPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: "transparent",
                color: "var(--fg-muted)",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              ✕ Zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Tabelle */}
      <Card padded={false}>
        {isLoading && <div style={{ padding: 24, fontSize: 13, color: "var(--fg-muted)" }}>Lade…</div>}
        {isError && (
          <div style={{ padding: 24, fontSize: 13, color: "var(--status-cancel)" }}>
            Buchungen konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !isError && allBookings.length === 0 && (
          <div style={{ padding: 24, fontSize: 13, color: "var(--fg-muted)" }}>Keine Buchungen gefunden.</div>
        )}

        {!isLoading && !isError && allBookings.length > 0 && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              {/* Kopfzeile */}
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)", background: "var(--bg-subtle)" }}>
                  <th style={thStyle}>REFERENZ</th>
                  <th style={thStyle}>GRUPPE</th>
                  <th style={thStyle}>ANSPRECHPARTNER</th>
                  <th style={thStyle}>ZEITRAUM</th>
                  <th style={thStyle}>EINHEITEN</th>
                  <th style={thStyle}>STATUS</th>
                </tr>
              </thead>

              {/* Datenzeilen */}
              <tbody>
                {paginatedBookings.map((b, i) => (
                  <TableBookingRow
                    key={b.id}
                    booking={b}
                    last={i === paginatedBookings.length - 1}
                    onRowClick={() => navigate(`/bookings/${b.id}`, { state: { from: location.pathname + location.search } })}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 18px",
                borderTop: "1px solid var(--border)",
                fontSize: 12,
                color: "var(--fg-muted)",
              }}
            >
              <span>{allBookings.length} Einträge</span>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    background: page === 1 ? "var(--bg-subtle)" : "var(--bg-card)",
                    color: "var(--fg)",
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    fontSize: 12,
                    fontWeight: 500,
                    opacity: page === 1 ? 0.5 : 1,
                  }}
                >
                  ← Vorherige
                </button>
                <span style={{ minWidth: 120, textAlign: "center" }}>
                  Seite {page} von {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    background: page === totalPages ? "var(--bg-subtle)" : "var(--bg-card)",
                    color: "var(--fg)",
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    fontSize: 12,
                    fontWeight: 500,
                    opacity: page === totalPages ? 0.5 : 1,
                  }}
                >
                  Nächste →
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function TableBookingRow({
  booking: b,
  last,
  onRowClick,
}: {
  booking: Booking;
  last: boolean;
  onRowClick: () => void;
}) {
  const n = naechte(b.check_in, b.check_out);
  const anzeigeName = [b.vorname, b.nachname].filter(Boolean).join(" ") || b.gastname || "";
  const anzeigeGruppe = b.gruppenname || b.gastname || "—";

  const einheitenArray = b.einheiten_namen;

  return (
    <tr
      style={{
        borderBottom: last ? "none" : "1px solid var(--border)",
        cursor: "pointer",
        transition: "background .12s",
      }}
      onClick={() => onRowClick()}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--bg-hover)")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      {/* REFERENZ */}
      <td style={tdStyle}>
        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-mono)" }}>
          {b.buchungsreferenz || "—"}
        </span>
      </td>

      {/* GRUPPE */}
      <td style={tdStyle}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {anzeigeGruppe}
        </div>
        {b.gast_typ && (
          <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
            {b.gast_typ}
          </div>
        )}
      </td>

      {/* ANSPRECHPARTNER */}
      <td style={tdStyle}>
        <div style={{ fontSize: 13 }}>
          {anzeigeName}
        </div>
        <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
          {b.email}
        </div>
      </td>

      {/* ZEITRAUM */}
      <td style={tdStyle}>
        <div style={{ fontSize: 13 }}>
          {formatDatum(b.check_in)} – {formatDatum(b.check_out)}
        </div>
        <div style={{ fontSize: 11, color: "var(--fg-muted)" }}>
          {n} Nächte · {b.gaesteanzahl} Pers.
        </div>
      </td>

      {/* EINHEITEN */}
      <td style={tdStyle}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {einheitenArray.map((name) => (
            <UnitChip key={name} name={name} />
          ))}
          {einheitenArray.length === 0 && <span style={{ color: "var(--fg-muted)" }}>—</span>}
        </div>
      </td>

      {/* STATUS */}
      <td style={tdStyle}>
        <StatusPill status={b.status} />
      </td>
    </tr>
  );
}

function MultiCheckDropdown<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  allLabel,
  disabled,
  disabledHint,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (vals: T[]) => void;
  allLabel: string;
  disabled?: boolean;
  disabledHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const active = selected.length > 0;
  const summary = !active
    ? allLabel
    : selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label ?? `${label} (1)`
      : `${label} (${selected.length})`;

  const toggle = (v: T) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        title={disabled ? disabledHint : undefined}
        disabled={disabled}
        style={{
          padding: "6px 10px",
          border: "1px solid var(--border)",
          borderRadius: 6,
          background: active ? "var(--accent)" : "var(--bg-card)",
          color: active ? "#fff" : "var(--fg)",
          fontFamily: "var(--font-body)",
          fontSize: 13,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
        }}
      >
        {summary}
        <span className="material-icons-outlined" style={{ fontSize: 16 }}>
          {open ? "expand_less" : "expand_more"}
        </span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            minWidth: 200,
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,.12)",
            padding: 4,
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {options.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--fg-muted)" }}>—</div>
          )}
          {options.map((o) => {
            const checked = selected.includes(o.value);
            return (
              <label
                key={String(o.value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--fg)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.value)}
                />
                {o.label}
              </label>
            );
          })}
          {active && (
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 4, paddingTop: 4 }}>
              <button
                type="button"
                onClick={() => onChange([])}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "6px 10px",
                  border: "none",
                  background: "transparent",
                  color: "var(--fg-muted)",
                  cursor: "pointer",
                  fontSize: 12,
                  borderRadius: 6,
                }}
              >
                ✕ Auswahl leeren
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Gemeinsame Styles
const thStyle = {
  padding: "12px 18px",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--fg-muted)",
  textAlign: "left" as const,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
  borderBottom: "2px solid var(--border)",
};

const tdStyle = {
  padding: "12px 18px",
  fontSize: 13,
  textAlign: "left" as const,
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle" as const,
};
